/* Sales · Bill Entry — manual counter-sale billing (walk-in customer).
 * A POS-style line grid: search a medicine, pick a live batch from stock,
 * enter qty / discount, see GST-inclusive line totals, then post the bill as
 * a sales invoice (POST /v1/sales, which decrements inventory).
 *
 * Reuses existing services — no new endpoints:
 *   searchMedicines       (ES typeahead)
 *   getMedicineBatches    (batch picker: live batches + loose-sale fields)
 *   postSalesInvoice      (create the sales invoice)
 * Follows the Supply Caliper UI kit, mirroring Grn.jsx / PurchaseOrder.jsx. */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import supplyService from "../../Services/supplyService";
import { useWarehouse } from "../Layout/WarehouseContext";
import { cbFmtINR, cbFmtNum, CB_PAYMENT_MODES } from "./supplyData";
import {
  buildCounterSaleInvoice,
  buildInvoiceFromSales,
  invoiceToPdfBlob,
  openPrintWindow,
  describeDocumentUploadError,
} from "./printInvoice";
import { useNavigate } from "react-router-dom";
import { PHARMACY_ROUTES } from "../../Route/path";
import {
  Button,
  Pill,
  Eyebrow,
  Segmented,
  SectionHeader,
  IconPlus,
  IconX,
  IconCheck,
  IconReceipt,
} from "./supplyKit";

/* Quantity unit-of-measure for a line — sell by full strip/pack or loose unit. */
const UOM_OPTIONS = [
  { id: "strip", label: "Strip" },
  { id: "unit", label: "Unit" },
];
const uomLabel = (uom) => (uom === "unit" ? "Unit" : "Strip");

/* Stock on a batch is two numbers, never one: `qtyA` counts WHOLE strips and
   `looseUnits` the units left over from a broken strip. Rendering just one of
   them (the old "10 in stock") tells the cashier the wrong thing, so always
   show both. */
/* Both shapes are accepted: a batch row from the picker (`qtyA`) and an entry /
   committed line, which snapshots the same figure as `stock`. */
const availStrips = (lot) => num(lot?.qtyA ?? lot?.stock);

const stockText = (lot) => {
  const strips = availStrips(lot);
  const loose = num(lot?.looseUnits);
  const stripPart = `${cbFmtNum(strips)} strip${strips === 1 ? "" : "s"}`;
  return loose > 0 ? `${stripPart} + ${cbFmtNum(loose)} loose` : stripPart;
};

/* Sellable ceiling for a line, in the unit the cashier is billing in:
   strips → whole strips only; units → every strip broken down plus the loose
   remainder (10 strips of 10 + 7 loose = 107 units). Without a known
   unitsPerStrip only the loose units can be sold by the unit. */
const maxQtyFor = (lot, uom) => {
  const strips = availStrips(lot);
  if (uom !== "unit") return strips;
  return strips * num(lot?.unitsPerStrip) + num(lot?.looseUnits);
};

const fieldInput = {
  width: "100%",
  height: 30,
  padding: "0 8px",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  background: "var(--bg-panel)",
  color: "var(--fg-default)",
  outline: "none",
};
const fieldMono = { ...fieldInput, fontFamily: "var(--font-mono)", textAlign: "right" };
const fieldLabel = {
  display: "block",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  marginBottom: 4,
};
const th = {
  padding: "5px 8px",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  textAlign: "left",
  borderBottom: "1px solid var(--border-default)",
  whiteSpace: "nowrap",
};
const td = { padding: "6px 8px", borderBottom: "1px solid var(--border-subtle)", verticalAlign: "middle" };
const iconBtn = (color) => ({
  border: 0,
  background: "transparent",
  cursor: "pointer",
  color,
  padding: 4,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const num = (v) => Number(v) || 0;
const round2 = (n) => Math.round((num(n) + Number.EPSILON) * 100) / 100;

/* Build a display name from a patient-search / patient-create row. */
const patientFullName = (p) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(" ").trim() || p?.name || "";

/* Rate a line actually bills at. S.Price / MRP are captured per STRIP (that's what
   the batch carries), so a line sold by the UNIT bills at that price divided by the
   batch's unitsPerStrip — the same derivation the backend does for a `UNIT` line.
   Without a known unitsPerStrip the strip price stands, which is the pre-loose-sale
   behaviour. */
const lineRates = (l) => {
  const per = l.uom === "unit" && num(l.unitsPerStrip) > 0 ? num(l.unitsPerStrip) : 1;
  return { sale: round2(num(l.salePrice) / per), mrp: round2(num(l.mrp) / per) };
};

/* Realised margin over what the batch cost — the line's Disc % is applied first,
   so this is the margin actually earned, not the one the S.Price implies.
   `per` cancels out of the ratio (both sides are per-strip, and a percentage
   discount is scale-free), so a UNIT line and a STRIP line off the same batch
   report the same margin. Returns null when the batch has no cost, so the UI can
   omit it rather than print a meaningless 0% or Infinity. */
const lineMargin = (l) => {
  const cost = num(l.purchase);
  if (!(cost > 0)) return null;
  const realised = num(l.salePrice) * (1 - num(l.disc) / 100);
  return round2(((realised - cost) / cost) * 100);
};

/* Margin on the price actually charged, the way the purchase team works it out:
     cost   = lot's landed cost per strip (scheme + trade discount out, free
              strips spread in) x (1 + GST %) — S.Price is GST-inclusive, so the
              cost carries GST too and the two compare like with like
     margin = (S.Price x (1 - Disc %) - cost) / (S.Price x (1 - Disc %))
   Only the line's own Disc % counts; membership and CureCoin are bill-level.
   A lot with no landed cost on record (opening stock, inwarded before IMS kept
   it) falls back to PTR x (1 + GST %) and is flagged `estimated`. With the
   supplier's discount left out that reads low — about 4 points on a typical 5%
   discount — so it is shown as an estimate only and does not drive the block:
   for those lots `loss` keeps the old rule, price below the bare PTR, so old
   stock is never blocked where it billed before. For a lot with a landed cost,
   `loss` is the true cost exceeding the price. Either way it also covers a 100%
   Disc, where there is no price to take a margin of. Returns null when the lot
   has no cost at all. */
const billMarginPct = (l) => {
  const landed = l.landed != null && Number.isFinite(Number(l.landed)) ? Number(l.landed) : null;
  const estimated = landed == null;
  const exGst = estimated ? num(l.purchase) : landed;
  if (estimated && !(exGst > 0)) return null;
  const cost = exGst * (1 + num(l.gst) / 100);
  const realised = num(l.salePrice) * (1 - num(l.disc) / 100);
  return {
    pct: realised > 0 ? ((realised - cost) / realised) * 100 : null,
    estimated,
    loss: estimated ? realised < exGst : cost > realised,
    cost,
    realised,
  };
};

/* Cut to 2 decimals without rounding (23.9956 -> "23.99"), matching the GRN
   margin. The tiny nudge keeps float noise from cutting a true 24.00 to 23.99. */
const trunc2 = (n) => (Math.trunc(n * 100 + Math.sign(n) * 1e-9) / 100).toFixed(2);

/* The batch's HSN goes onto the invoice as it is. New GRNs can't post a bad one
   any more (6 or 8 digits, same rule as the GRN screen), but older batches still
   carry blank or malformed codes. Warn only: blocking would stop those batches
   from being sold at all. */
const hasValidHsn = (l) => /^(\d{6}|\d{8})$/.test(String(l.hsn ?? "").trim());

/* Per-line base math. S.Price is treated as GST-inclusive (MRP-style), matching
   the way printInvoice.js bills lines. Two discount figures are derived:
     · userDisc — the cashier's per-line Disc%, an actual reduction in payable.
     · mrpDisc  — the MRP→S.Price markdown (a saving off MRP, not a reduction in
                  what the customer pays).
   productDiscountAmount (sent to /v1/sales) = mrpDisc + userDisc, matching the
   online order create-bill payload's "MRP markdown + extra discount" meaning. */
const lineBase = (l) => {
  const qty = num(l.qty);
  const { sale, mrp } = lineRates(l);
  const gross = round2(sale * qty);
  const userDisc = round2((gross * num(l.disc)) / 100);
  const mrpDisc = round2(Math.max(0, mrp - sale) * qty);
  const prodDisc = round2(mrpDisc + userDisc);
  const rate = num(l.gst);
  return { qty, sale, mrp, gross, userDisc, mrpDisc, prodDisc, rate };
};

/* CureCoin is redeemable only against GX products — the medicine search marks
   them with preferredMedicine: "gx". A line inherits that flag on select, so
   coin never lands on a branded/non-GX item. */
const isGxCode = (flag) => String(flag || "").trim().toLowerCase() === "gx";
const isGxLine = (l) => isGxCode(l?.preferredMedicine);

/* Earliest expiry first. The counter should move the batch that dies first, so
   this is the order the batch list is shown in and the one auto-pick follows. */
const byExpiry = (a, b) => String(a?.exp || "").localeCompare(String(b?.exp || ""));

/* The entry-row fields a picked lot contributes. One definition, used by the
   batch dropdown, by the auto-pick after a product search, and by the refresh
   that follows the live batch fetch — three paths that must not drift apart. */
const lotFields = (lot, prevUom) =>
  lot
    ? {
      lotId: lot.id,
      batch: lot.batch,
      exp: lot.exp,
      rack: lot.rackCode || "",
      stock: num(lot.qtyA),
      /* Loose-unit stock + whether this batch may be sold by the unit at all.
         Selling by unit is gated on looseSaleSupported alone; a batch with no
         loose remainder can still be broken open. Fall back to Strip when the
         new batch doesn't support it, so the line can't keep a stale UNIT. */
      unitsPerStrip: lot.unitsPerStrip ?? null,
      looseUnits: num(lot.looseUnits),
      looseSaleSupported: Boolean(lot.looseSaleSupported),
      uom: lot.looseSaleSupported ? prevUom : "strip",
      mrp: lot.mrp,
      salePrice: lot.sale || lot.mrp,
      purchase: lot.purchase,
      landed: lot.landed ?? null,
      hsn: lot.hsnCode || "", // HSN is per-batch (fromInventoryRow) — carry it to the invoice
      /* GST is per-batch too, but a lot that carries no rate must not zero the
         line — every caller falls back to the rate already on the row, so it
         is applied at the call site rather than here. */
    }
    : {
      lotId: "",
      batch: "",
      exp: "",
      rack: "",
      stock: 0,
      unitsPerStrip: null,
      looseUnits: 0,
      looseSaleSupported: false,
      uom: "strip",
    };

/* Roll the lines up into a bill, applying a bill-level membership discount (%)
   and an applied CureCoin amount (₹). Membership and CureCoin are real counter
   reductions: they lower each line's net (and so its taxable value + GST).
   Membership spreads over every line proportional to the post-cashier-discount
   net; CureCoin spreads only over the GX lines (and is capped at what those
   lines still owe). Each /v1/sales item then carries its own
   membershipDiscountAmount / cureCoinDiscount, exactly like the online payload. */
const computeBill = (lines, membershipPct = 0, cureCoinApplied = 0) => {
  const rows = lines.map((l) => ({ l, ...lineBase(l) }));
  const mPct = num(membershipPct);

  rows.forEach((r) => {
    r.sellNet = round2(r.gross - r.userDisc); // payable before membership/CureCoin
    r.memberDisc = round2((r.sellNet * mPct) / 100);
  });
  const memberTotal = round2(rows.reduce((s, r) => s + r.memberDisc, 0));

  /* CureCoin can only offset the GX lines, and only what they still owe after
     the membership cut. No GX line on the bill => no coin is applied at all. */
  const gxRows = rows.filter((r) => isGxLine(r.l));
  const gxAfterMember = round2(gxRows.reduce((s, r) => s + round2(r.sellNet - r.memberDisc), 0));
  const coinTotal = round2(Math.max(0, Math.min(num(cureCoinApplied), gxAfterMember)));
  let coinAssigned = 0;
  rows.forEach((r) => {
    r.coinDisc = 0;
  });
  gxRows.forEach((r, i) => {
    if (i === gxRows.length - 1) {
      r.coinDisc = round2(coinTotal - coinAssigned); // last GX line absorbs the rounding
    } else {
      const lineAfterMember = round2(r.sellNet - r.memberDisc);
      r.coinDisc = gxAfterMember > 0 ? round2((lineAfterMember / gxAfterMember) * coinTotal) : 0;
      coinAssigned = round2(coinAssigned + r.coinDisc);
    }
  });

  rows.forEach((r) => {
    r.net = round2(r.sellNet - r.memberDisc - r.coinDisc);
    r.taxable = round2(r.net / (1 + r.rate / 100));
    r.gstAmt = round2(r.net - r.taxable);
  });

  const sum = (f) => round2(rows.reduce((s, r) => s + num(f(r)), 0));
  const net = sum((r) => r.net);
  const netRounded = Math.round(net);
  return {
    rows,
    items: lines.length,
    qty: rows.reduce((s, r) => s + r.qty, 0),
    gross: sum((r) => r.gross),
    prodDisc: sum((r) => r.prodDisc),
    member: memberTotal,
    coin: coinTotal,
    taxable: sum((r) => r.taxable),
    gst: sum((r) => r.gstAmt),
    net,
    roundOff: round2(netRounded - net),
    payable: netRounded,
  };
};

/* Per-line CureCoin off a v1/calculations/sale line. The backend applies the
   coin inside lineTotal but doesn't always echo it back as its own field, so:
   take the field when it's there, else recover it from the money identity
   (net = gross − cashier disc − membership − coin), and never trust more than
   the GX-only amount we asked for. Without this the bill would foot right while
   reporting ₹0 CureCoin. */
const pricedCoinDisc = (pl, l, requested) => {
  const echoed = num(pl.cureCoinDiscount ?? pl.cureCoinDiscountAmount ?? pl.coinDiscount);
  if (echoed > 0) return round2(echoed);
  const userDisc = round2((num(pl.gross) * num(l.disc)) / 100);
  const implied = round2(num(pl.gross) - userDisc - num(pl.membershipDiscountAmount) - num(pl.lineTotal));
  return round2(Math.max(0, Math.min(implied, num(requested))));
};

const blankEntry = () => ({
  medId: "",
  medicineName: "",
  preferredMedicine: "", // "gx" => CureCoin-eligible (from the medicine search record)
  lotId: "",
  batch: "",
  exp: "",
  /* Storage rack of the picked batch, straight off the lot. Display only — it
     never goes to /v1/sales (the sale does not move stock between racks), but it
     prints on the invoice so counter staff know where to fetch from. */
  rack: "",
  stock: 0, // whole strips available on the picked batch
  /* Loose-unit stock on the picked batch (see stockText / maxQtyFor). */
  unitsPerStrip: null,
  looseUnits: 0,
  looseSaleSupported: false,
  mrp: "",
  salePrice: "",
  /* Batch cost, carried purely to show the margin on the line. */
  purchase: "",
  /* Landed cost per strip ex-GST (null = not on record, margin falls back to PTR). */
  landed: null,
  qty: "",
  uom: "strip", // 'strip' (full pack) | 'unit' (loose)
  disc: "",
  gst: 5,
  hsn: "", // HSN code, copied from the picked batch (per-lot, like GST)
});

const SalesBillEntry = () => {
  /* The store being billed for is the one selected app-wide in the topbar — a
     cashier's own, or whichever a head switched to. Never defaulted from a list:
     billing into a store nobody chose is a wrong sale, so an empty selection
     blocks the post instead. */
  const { code: warehouseId, isAll } = useWarehouse();
  const [customer, setCustomer] = useState({ name: "", mobile: "", doctorId: "" });
  /* Prescribing-doctor master (warehouse-scoped) for the required doctor picker.
     "Add new doctor" in the dropdown routes to the Doctors module. */
  const [doctors, setDoctors] = useState([]);
  const navigate = useNavigate();
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [lines, setLines] = useState([]);
  const [entry, setEntry] = useState(blankEntry());
  const [editIdx, setEditIdx] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /* Patient membership (from the membership-details API) + CureCoin applied, set
     by the "Compute discount" button and fed into the bill discount math. */
  const [membership, setMembership] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [cureCoin, setCureCoin] = useState("");
  /* Store credit (from sales returns refunded to CUSTOMER_CREDIT). Looked up by
     the bill's mobile — the counter sale sends no patientId, so the phone is the
     key the redemption will resolve to; asking by anything else would show a
     balance this bill cannot spend. `creditUse` is what the cashier chose to
     redeem. It is a TENDER, not a discount: GST and net payable don't move, it
     only changes how the payable is paid. */
  const [credit, setCredit] = useState({ status: "idle", balance: 0, linked: [], error: "" });
  const [creditUse, setCreditUse] = useState("");
  /* Printable-bill preview shown after a successful post (mirrors the online
     sales-order flow): the operator reviews the invoice, then Print uploads the
     PDF and opens the browser print window. */
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [printingInvoice, setPrintingInvoice] = useState(false);
  /* Backend-authoritative pricing (POST v1/calculations/sale). The browser sends
     raw line inputs + membership% + CureCoin and renders the returned money
     (incl. the per-line product/membership/CureCoin discount split). */
  const [priced, setPriced] = useState({ lines: [], totals: null, warnings: [] });
  const [pricing, setPricing] = useState(false);
  const [priceErr, setPriceErr] = useState(null);

  const membershipPct = num(membership?.membershipDiscount);

  /* Load the warehouse's active doctors for the required picker and pre-select
     the default doctor (if one is marked) when nothing is chosen yet. */
  useEffect(() => {
    let alive = true;
    supplyService
      .getDoctors({ warehouseId: warehouseId || undefined })
      .then((list) => {
        if (!alive) return;
        const active = (list || []).filter((d) => d.isActive !== false);
        setDoctors(active);
        const def = active.find((d) => d.isDefault);
        if (def) setCustomer((c) => (c.doctorId ? c : { ...c, doctorId: def.id }));
      })
      .catch(() => {
        if (alive) setDoctors([]);
      });
    return () => {
      alive = false;
    };
  }, [warehouseId]);

  /* Balance lookup, debounced on the mobile. Only a full 10-digit number is
     asked about — a half-typed one would just return someone else's zero. */
  useEffect(() => {
    const phone = customer.mobile.trim();
    setCreditUse("");
    if (!warehouseId || !/^\d{10}$/.test(phone)) {
      setCredit({ status: "idle", balance: 0, linked: [], error: "" });
      return undefined;
    }
    let alive = true;
    setCredit((c) => ({ ...c, status: "loading", error: "" }));
    const t = setTimeout(() => {
      supplyService
        .getCustomerCreditBalance({ warehouseId, customerPhone: phone })
        .then((res) => {
          if (!alive) return;
          setCredit({ status: "ok", balance: round2(res.balance), linked: res.linkedAccounts, error: "" });
        })
        .catch((e) => {
          if (!alive) return;
          setCredit({
            status: "error",
            balance: 0,
            linked: [],
            error: e?.response?.data?.message || e?.message || "Could not load store credit.",
          });
        });
    }, 350);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [customer.mobile, warehouseId]);

  /* The default doctor id (used to reset the picker back to it after a sale). */
  const defaultDoctorId = useMemo(() => doctors.find((d) => d.isDefault)?.id || "", [doctors]);

  /* "Add patient" popup — opened from the search dropdown when no existing
     customer matches. Prefilled with whatever the cashier already typed. */
  const [addPatient, setAddPatient] = useState(null); // { name, mobile } | null

  const setC = (key, val) => setCustomer((c) => ({ ...c, [key]: val }));
  const setE = (key, val) => setEntry((e) => ({ ...e, [key]: val }));

  /* Fill the customer from a picked / newly-created patient. A changed customer
     invalidates any previously-looked-up membership. */
  const applyPatient = (p) => {
    if (!p) return;
    setCustomer((c) => ({
      ...c,
      name: patientFullName(p) || c.name,
      mobile: p.mobile || c.mobile,
    }));
    setMembership(null);
  };

  /* Load live batches for the medicine currently in the entry row — from the batch
     picker endpoint (v1/inventory/medicine/{code}/batches), which is the one that
     carries the loose-sale fields (unitsPerStrip / looseUnitsAvailable /
     looseSaleSupported) this screen needs to offer UNIT billing. */
  useEffect(() => {
    if (!entry.medId) {
      setBatches([]);
      return undefined;
    }
    let alive = true;
    setBatchLoading(true);
    supplyService
      .getMedicineBatches(entry.medId, { warehouseId })
      .then((items) => {
        if (!alive) return;
        const live = [...(items || [])].sort(byExpiry);
        setBatches(live);
        setBatchLoading(false);
        /* The product search already seeded a batch from its own rows; this is
           the authoritative list arriving a moment later. Keep the operator's
           batch if it is still there — re-applying it would undo a price they
           may have edited — and only auto-pick when nothing valid is selected. */
        setEntry((e) => {
          const stillThere = live.find((b) => String(b.id) === String(e.lotId));
          if (stillThere) return e;
          return { ...e, ...lotFields(live[0] || null, e.uom), gst: live[0]?.gstPct || e.gst };
        });
      })
      .catch(() => {
        if (!alive) return;
        setBatches([]);
        setBatchLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [entry.medId, warehouseId]);

  const onSelectMedicine = (med) => {
    if (!med) {
      setEntry((e) => ({
        ...e,
        medId: "",
        medicineName: "",
        preferredMedicine: "",
        lotId: "",
        batch: "",
        exp: "",
        stock: 0,
        mrp: "",
        salePrice: "",
      }));
      return;
    }
    /* The search rows ARE batch rows, so the batch list is already in hand —
       fill it now rather than leaving the row blank until the fetch returns. */
    const seeded = [...(med.batches || [])].sort(byExpiry);
    setBatches(seeded);
    setEntry((e) => ({
      ...e,
      medId: med.medId,
      medicineName: med.medicineName,
      /* GX marker — decides CureCoin eligibility. The stock search has no such
         flag (it is a catalogue attribute, not a lot one), so it is looked up
         separately below and lands a moment later. */
      preferredMedicine: "",
      /* Earliest-expiry batch, pre-picked. FEFO is what the counter should sell
         and what the batch dropdown lists first; any other batch is one click. */
      ...lotFields(seeded[0] || null, e.uom),
      gst: seeded[0]?.gstPct || e.gst,
    }));

    /* Best-effort: the catalogue knows whether this is a GX product, the stock
       table does not. A miss (or a search service that is down) must not block
       billing — it only means CureCoin cannot be applied to this line. */
    supplyService
      .getPreferredMedicineFlag(med.medId)
      .then((flag) => {
        if (!flag) return;
        setEntry((e) => (e.medId === med.medId ? { ...e, preferredMedicine: flag } : e));
        /* The flag lands a few hundred ms after the pick and this screen is
           keyboard-first, so the line can already be committed by then — and
           commitEntry resets the entry row, which makes the setEntry above a
           no-op. Patch the committed lines too, or a quick Add drops GX (and the
           CureCoin that rides on it) for good, with nothing on screen saying why. */
        setLines((prev) =>
          prev.some((l) => l.medId === med.medId && !l.preferredMedicine)
            ? prev.map((l) =>
              l.medId === med.medId && !l.preferredMedicine ? { ...l, preferredMedicine: flag } : l
            )
            : prev
        );
      })
      .catch(() => { });
  };

  /* Picking a batch copies its price / MRP / expiry / available stock. */
  const onPickBatch = (lotId) => {
    const lot = batches.find((b) => String(b.id) === String(lotId));
    setEntry((e) => ({ ...e, ...lotFields(lot || null, e.uom), gst: lot?.gstPct || e.gst }));
  };

  /* Ceiling for the qty box in whichever unit the line is billed in. */
  const entryMaxQty = maxQtyFor(entry, entry.uom);

  /* `entry.salePrice` is stored per STRIP — that is what the batch carries and
     what lineRates()/the backend divide down for a UNIT line. The S.Price box,
     though, must show the price the line actually bills at, or a cashier selling
     loose units reads a strip price and edits it as if it were per-unit.
     Display divides by unitsPerStrip; an edit multiplies back so storage stays
     per-strip and the rest of the math is untouched. */
  const entryPerUnit = entry.uom === "unit" && num(entry.unitsPerStrip) > 0 ? num(entry.unitsPerStrip) : 1;
  const entrySalePriceShown =
    entry.salePrice === "" || entry.salePrice == null
      ? ""
      : entryPerUnit > 1
        ? round2(num(entry.salePrice) / entryPerUnit)
        : entry.salePrice;
  const setEntrySalePriceShown = (v) =>
    setE("salePrice", v === "" ? "" : entryPerUnit > 1 ? round2(num(v) * entryPerUnit) : v);

  const entryValid =
    entry.medId &&
    entry.lotId &&
    num(entry.qty) > 0 &&
    num(entry.qty) <= entryMaxQty &&
    num(entry.salePrice) > 0;

  const commitEntry = () => {
    if (!entryValid) return;
    if (editIdx !== null) {
      setLines((prev) => prev.map((l, i) => (i === editIdx ? entry : l)));
      setEditIdx(null);
    } else {
      setLines((prev) => [...prev, entry]);
    }
    setEntry(blankEntry());
  };

  const editLine = (i) => {
    setEntry(lines[i]);
    setEditIdx(i);
  };
  const delLine = (i) => {
    setLines((prev) => prev.filter((_, idx) => idx !== i));
    if (editIdx === i) {
      setEditIdx(null);
      setEntry(blankEntry());
    }
  };

  /* Debounced authoritative pricing. Sends raw line inputs + membership% (as the
     bill discount) + CureCoin to POST v1/calculations/sale and stores the money
     it returns. The bill-level CureCoin is split across the GX lines only (via
     computeBill, so the split matches the local math) purely to fill the
     request's per-line cureCoinDiscount input; the server returns the
     authoritative, capped per-line amounts. */
  useEffect(() => {
    if (!lines.length) {
      setPriced({ lines: [], totals: null, warnings: [] });
      setPriceErr(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setPricing(true);
      setPriceErr(null);
      const coinSplit = computeBill(lines, membershipPct, num(cureCoin)).rows.map((r) => r.coinDisc);
      supplyService
        .computeSalePricing({
          warehouseId,
          customerState: undefined, // TODO: source customer state for intra/inter-state GST
          billDiscount: { type: "PCT", value: membershipPct },
          lines: lines.map((l, i) => ({
            ref: String(i),
            inventoryId: Number(l.lotId) || 0,
            medId: l.medId,
            batchNo: l.batch,
            qty: num(l.qty),
            /* Strips or loose units — the backend prices a UNIT line off the
               per-unit rate derived from the batch's unitsPerStrip. Same field
               the /v1/sales create payload sends. */
            unitOfMeasure: (l.uom || "strip").toUpperCase(),
            price: num(l.salePrice),
            mrp: num(l.mrp),
            lineDiscount: { type: "PCT", value: num(l.disc) },
            gstOverride: num(l.gst),
            /* GX-only: non-GX lines go out with 0 CureCoin. */
            cureCoinDiscount: round2(num(coinSplit[i])),
          })),
        })
        .then((res) => {
          if (cancelled) return;
          setPriced({
            lines: res?.lines || [],
            totals: res?.totals || null,
            warnings: res?.warnings || [],
          });
          setPricing(false);
        })
        .catch((e) => {
          if (cancelled) return;
          setPriceErr(e?.response?.data?.message || e?.message || "Could not price the bill.");
          setPricing(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [lines, membershipPct, cureCoin, warehouseId]);

  /* Bill shaped exactly like computeBill's output, but sourced from the backend
     so render / POST payload / printed invoice all consume authoritative money.
     Falls back to local computeBill for display only while pricing is in flight
     or unavailable (never used for posting — see canCreate). */
  const bill = useMemo(() => {
    /* The GX-only CureCoin split we asked the backend for — also the fallback
       when the response doesn't echo the per-line coin back (see below). */
    const local = computeBill(lines, membershipPct, num(cureCoin));
    const t = priced.totals;
    if (t && (priced.lines || []).length === lines.length) {
      const byRef = {};
      priced.lines.forEach((pl) => {
        byRef[String(pl.ref)] = pl;
      });
      const rows = lines.map((l, i) => {
        const pl = byRef[String(i)] || {};
        /* Rates are per billed unit (per-unit for a UNIT line) so the grid and the
           printed bill read `rate × qty = gross` against the backend's figures. */
        const rates = lineRates(l);
        return {
          l,
          qty: num(l.qty),
          sale: rates.sale,
          mrp: rates.mrp,
          gross: num(pl.gross),
          prodDisc: round2(num(pl.productDiscountAmount)),
          memberDisc: round2(num(pl.membershipDiscountAmount)),
          coinDisc: pricedCoinDisc(pl, l, num(local.rows[i]?.coinDisc)),
          taxable: round2(num(pl.taxableAmount)),
          gstAmt: round2(num(pl.cgstAmount) + num(pl.sgstAmount) + num(pl.igstAmount)),
          rate: num(pl.gstRate),
          net: round2(num(pl.lineTotal)),
        };
      });
      const sum = (f) => round2(rows.reduce((s, r) => s + num(f(r)), 0));
      return {
        rows,
        items: lines.length,
        qty: rows.reduce((s, r) => s + r.qty, 0),
        gross: round2(t.grossAmount),
        prodDisc: sum((r) => r.prodDisc),
        member: sum((r) => r.memberDisc),
        coin: sum((r) => r.coinDisc),
        taxable: round2(t.taxableAmount),
        gst: round2(num(t.cgstAmount) + num(t.sgstAmount) + num(t.igstAmount)),
        net: round2(t.netBeforeRound),
        roundOff: round2(t.roundOff),
        payable: round2(t.netAmount),
      };
    }
    return local;
  }, [priced, lines, membershipPct, cureCoin]);

  /* Store credit actually redeemed: what the cashier asked for, capped by the
     balance and by the bill (credit can't be paid out as change). The rest is
     paid by the chosen payment mode. */
  const creditBalance = credit.status === "ok" ? credit.balance : 0;
  const creditCap = round2(Math.max(0, Math.min(creditBalance, bill.payable)));
  const creditApplied = round2(Math.max(0, Math.min(num(creditUse), creditCap)));
  const balanceDue = round2(bill.payable - creditApplied);

  const hasLossLine = lines.some((l) => {
    const m = billMarginPct(l);
    return m != null && m.loss;
  });

  /* Posting requires authoritative backend pricing — never post on the
     transitional fallback numbers or while a price call is pending/failed. */
  const canCreate =
    !hasLossLine &&
    /* A head must pick the store being billed from before anything can post. */
    !!warehouseId &&
    lines.length > 0 &&
    !!customer.name.trim() &&
    !!customer.mobile.trim() &&
    !!customer.doctorId &&
    !saving &&
    !pricing &&
    !priceErr &&
    !!priced.totals &&
    /* Don't redeem against a balance we haven't confirmed for this mobile. */
    !(num(creditUse) > 0 && credit.status !== "ok");

  const reset = () => {
    setLines([]);
    setEntry(blankEntry());
    setEditIdx(null);
    /* Keep the default doctor selected for the next walk-in. */
    setCustomer({ name: "", mobile: "", doctorId: defaultDoctorId });
    setMembership(null);
    setCureCoin("");
    setCreditUse("");
  };

  /* Affinity's loyalty summary is asked about the GX lines only — CureCoin isn't
     redeemable on branded stock, and Affinity scores a line only when its
     categoryCode is "Generic" (which is what GX maps to).

     It computes points as Σ qty × (mrp − discount), so `discount` must be the
     real per-unit markdown: MRP down to what the customer actually pays for
     that unit after the cashier's Disc% and the membership cut. `totalBill` is
     the same GX slice as a bill figure. Both are taken before any CureCoin so
     the quote can't feed back on itself.
     Returns null when the bill has no GX line — nothing to ask about. */
  const buildLoyaltyRequest = (pct) => {
    const gx = computeBill(lines, pct, 0).rows.filter((r) => isGxLine(r.l));
    if (!gx.length) return null;
    const doctor = doctors.find((d) => String(d.id) === String(customer.doctorId));
    const linePayable = (r) => round2(r.sellNet - r.memberDisc);
    return {
      name: customer.name || "Counter Sale",
      phoneNumber: customer.mobile.trim(),
      /* No invoice number exists until the sale posts, and create_bill=false
         makes this a quote — a provisional reference is enough. */
      billNumber: `PREVIEW-${warehouseId || "NA"}-${Date.now()}`,
      storeCode: warehouseId,
      doctorName: doctor?.name || "",
      totalBill: round2(gx.reduce((s, r) => s + linePayable(r), 0)),
      medicineDetail: gx.map((r, i) => {
        const qty = num(r.l.qty);
        const unitPaid = qty > 0 ? linePayable(r) / qty : 0;
        return {
          id: String(i + 1),
          name: r.l.medicineName,
          productId: r.l.medId,
          qty,
          gst: num(r.l.gst),
          categoryCode: "Generic", // GX is the generic range — the only code Affinity scores
          mrp: num(r.l.mrp),
          discount: round2(Math.max(0, num(r.l.mrp) - unitPaid)),
          batchNo: r.l.batch || null,
          hsnCode: r.l.hsn || null,
        };
      }),
    };
  };

  /* Look up the customer's membership (discount %) and ask Affinity how much
     CureCoin this bill's GX lines are eligible for, then pre-fill the CureCoin
     field with those points — capped at what the GX lines still owe after the
     membership cut. The two lookups are independent: one failing still applies
     the other. */
  const handleComputeDiscount = async () => {
    const mobile = customer.mobile.trim();
    if (!mobile) {
      toast.info("Enter the customer mobile to look up membership.");
      return;
    }
    if (!lines.length) {
      toast.info("Add at least one product before computing the discount.");
      return;
    }
    setMemberLoading(true);
    let memberErr = null;
    let loyaltyErr = null;
    let info = null;
    try {
      info = await supplyService.getPatientMembership(mobile);
    } catch (e) {
      /* v1/patient/membership isn't deployed everywhere — a 404 means "no
         membership discount available here", not a failure worth alarming the
         cashier over (and it must not stop the CureCoin lookup). */
      if (e?.response?.status !== 404) memberErr = e;
    }
    const isMember = !!info?.isMember;
    const pct = isMember ? num(info.membershipDiscount) : 0;
    setMembership(isMember ? info : null);

    /* CureCoin is Affinity's call, not the membership record's. */
    const req = buildLoyaltyRequest(pct);
    let coin = 0;
    let eligible = 0;
    if (req) {
      try {
        const summary = await supplyService.getBillLoyaltySummary(req);
        eligible = num(summary.loyaltyPoints);
        coin = round2(computeBill(lines, pct, eligible).coin);
      } catch (e) {
        loyaltyErr = e;
      }
    }
    setCureCoin(coin > 0 ? String(coin) : "");
    setMemberLoading(false);

    if (memberErr) {
      toast.error(memberErr?.response?.data?.message || memberErr?.message || "Could not fetch membership details.");
    }
    if (loyaltyErr) {
      toast.error(loyaltyErr?.response?.data?.message || loyaltyErr?.message || "Could not fetch CureCoin eligibility.");
    }
    const bits = [isMember ? `${info.membershipName || "Member"} · ${pct}% off` : "No active membership"];
    if (coin > 0) bits.push(`₹${coin} CureCoin applied on GX items`);
    else if (!req) bits.push("no GX product for CureCoin");
    else if (!loyaltyErr && eligible <= 0) bits.push("no CureCoin eligible");
    if (isMember || coin > 0) toast.success(`${bits.join(" · ")}.`);
    else if (!memberErr && !loyaltyErr) toast.info(`${bits.join(" · ")}.`);
  };

  /* Post the counter sale as a sales invoice. Same /v1/sales body the online
     order create-bill flow uses (transactionId, paymentMode, and the per-item
     productDiscountAmount / membershipDiscountAmount / cureCoinDiscount ₹
     figures), minus an orderId — this is a walk-in, not an online order. */
  const handleCreate = async () => {
    setSaving(true);
    const selectedDoctor = doctors.find((d) => String(d.id) === String(customer.doctorId));
    const payload = {
      warehouseId,
      orderId: "",
      transactionId: "",
      paymentMode: creditApplied > 0 && balanceDue <= 0 ? "CUSTOMER_CREDIT" : paymentMode,
      /* Only when store credit is redeemed: the tenders, which must add up to the
         net payable exactly. Without credit the bill goes out the old way — one
         tender of paymentMode for the whole net — so nothing changes for it. */
      ...(creditApplied > 0
        ? {
          payments: [
            { paymentMode: "CUSTOMER_CREDIT", amount: creditApplied },
            ...(balanceDue > 0 ? [{ paymentMode, amount: balanceDue }] : []),
          ],
        }
        : {}),
      customerName: customer.name || "Counter Sale",
      customerPhone: customer.mobile || "",
      /* Required prescribing doctor — send the master id + resolved name. */
      doctorId: customer.doctorId ? Number(customer.doctorId) : undefined,
      doctorName: selectedDoctor?.name || "",
      remarks: "counter sale",
      items: bill.rows.map((r) => ({
        inventoryId: Number(r.l.lotId),
        medicineCode: r.l.medId,
        batchNo: r.l.batch,
        /* Per-batch HSN carried through so the stored invoice (and its reprint)
           taxes each line under the correct code. */
        hsnCode: r.l.hsn || undefined,
        quantity: num(r.l.qty),
        fulfilledQuantity: num(r.l.qty),
        /* Whether the quantity is counted in full strips/packs or loose units. */
        unitOfMeasure: (r.l.uom || "strip").toUpperCase(),
        /* Cashier's per-line discount as ₹ only. The percentage is deliberately
           NOT sent: the rate and the amount are two statements of the same thing,
           and shipping both invites the backend to apply whichever it prefers —
           which diverges the moment rounding differs (a 7-unit line at 3% is
           ₹26.98, not 26.9784). The amount is the figure the bill was computed
           from, so it is the one that goes. */
        discountAmount: round2((num(r.gross) * num(r.l.disc)) / 100),
        orderDetailsId: 0,
        /* Each discount as its own ₹ amount (the type is implied by which is
           non-zero), exactly like the online order create-bill items. */
        productDiscountAmount: round2(r.prodDisc),
        membershipDiscountAmount: round2(r.memberDisc),
        cureCoinDiscount: round2(r.coinDisc),
      })),
    };
    try {
      const res = await supplyService.postSalesInvoice({ data: payload });
      toast.success(
        creditApplied > 0
          ? `Counter sale billed — ${cbFmtINR(bill.payable)} (${cbFmtINR(creditApplied)} from store credit).`
          : `Counter sale billed — ${cbFmtINR(bill.payable)}.`
      );
      /* Capture the backend's invoiceId (for the document upload on Print) and
         its authoritative invoiceNumber / invoiceDate, then render the printable
         bill from the same rows the cashier saw — exactly like the online sales
         order's invoice preview. */
      const created = res?.data ?? res ?? {};
      const invoiceId = created.invoiceId ?? created.id ?? null;

      /* Render the invoice the backend just created, NOT the local preview.
         The server applies discounts the client never computes (this bill went
         out with a 0 cashier discount and came back with discountPercentage
         14.99 / productDiscountAmount 3.21), so printing `bill.rows` showed
         "Product Discount ₹0.00" on an invoice that had actually been
         discounted. Same treatment AllocationBilling already gives its bills.
         Only payment mode / doctor need topping up — the counter screen holds
         no patient age or gender to enrich with. */
      const enriched = {
        ...created,
        paymentMode: created.paymentMode || paymentMode,
        doctorName: created.doctorName || selectedDoctor?.name || "",
      };
      const mapped = supplyService.normalizeSalesInvoice(enriched);
      /* The sales invoice DTOs carry no rack — placement lives apart from the sale,
         and the backend has no reason to echo it. So the rack each line was picked
         from is merged back on here, keyed by lot id (which the response does
         carry). Without this the printed bill loses the rack the cashier saw on
         screen, because what gets printed is the backend's invoice, not the local
         preview. */
      if (mapped?.items?.length) {
        const rackByLot = new Map(
          bill.rows
            .filter((r) => r.l?.rack)
            .map((r) => [String(r.l.lotId), r.l.rack])
        );
        mapped.items = mapped.items.map((it) => ({
          ...it,
          rackCode: it.rackCode || rackByLot.get(String(it.inventoryId)) || "",
        }));
      }
      const fromSales = mapped?.items?.length ? buildInvoiceFromSales(mapped) : null;

      /* Fall back to the client-side build only when the response can't be
         rendered — it still shows the cashier what was billed. */
      const invoice =
        fromSales ||
        buildCounterSaleInvoice({
          rows: bill.rows,
          customer: { ...customer, doctorName: selectedDoctor?.name || "" },
          paymentMode,
          overrides: {
            billNo: created.invoiceNumber,
            billDateTime: created.invoiceDate || undefined,
            txnId: created.transactionId || "",
          },
        });
      if (invoice) {
        setInvoicePreview({ ...invoice, invoiceId });
      } else {
        reset();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not create the sale.");
    } finally {
      setSaving(false);
    }
  };

  /* Print from the preview: render the previewed invoice HTML to a PDF, attach
     it to the sales record (when we have the invoiceId), then open the print
     window. The same PDF the operator sees is what gets stored. */
  const handlePreviewPrint = () => {
    if (!invoicePreview) return;
    setPrintingInvoice(true);
    /* Attaching the PDF and printing it are separate concerns, and only one of
       them the customer is waiting for. The sale is already posted by this point,
       so a failed attachment must not cost the operator the printout — it warns and
       prints anyway. Re-attaching later is possible from Bill History. */
    const attach =
      invoicePreview.invoiceId != null
        ? invoiceToPdfBlob(invoicePreview).then((file) =>
          supplyService
            .uploadSalesDocument(invoicePreview.invoiceId, file)
            .catch((e) => {
              toast.warn(describeDocumentUploadError(e, file));
            })
        )
        : Promise.resolve(null);
    attach
      .then(() => {
        openPrintWindow(invoicePreview);
        setPrintingInvoice(false);
        setInvoicePreview(null);
        reset();
      })
      .catch((e) => {
        /* Only a failure to RENDER the PDF lands here — there is nothing to print. */
        setPrintingInvoice(false);
        toast.error(e?.message || "Could not render the invoice for printing.");
      });
  };

  /* Closing the preview without printing still finalises the (already-posted)
     sale — clear the counter for the next customer. */
  const handlePreviewClose = () => {
    if (printingInvoice) return;
    setInvoicePreview(null);
    reset();
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Bill Entry"
        sub="Counter sale · walk-in billing · posts a sales invoice and decrements stock"
      />

      {/* Customer strip */}
      <div
        style={{
          background: "var(--bg-panel)",
          border: "1px solid var(--border-default)",
          borderRadius: 6,
          padding: "12px 16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <Eyebrow>Customer (optional for walk-in)</Eyebrow>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {credit.status === "loading" && (
              <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>Checking store credit…</span>
            )}
            {credit.status === "error" && (
              <span style={{ fontSize: 11, color: "var(--amber-700)" }} title={credit.error}>
                Store credit unavailable
              </span>
            )}
            {credit.status === "ok" && (
              <Pill variant={credit.balance > 0 ? "green" : "neutral"}>
                Store credit · {cbFmtINR(credit.balance)}
              </Pill>
            )}
            {credit.status === "ok" && credit.linked.some((a) => a.balance > 0) && (
              <span
                style={{ fontSize: 11, color: "var(--amber-700)" }}
                title="Another store-credit account this phone reaches (keyed on a patient id). It can't be spent on a counter bill until the accounts are merged."
              >
                +{cbFmtINR(credit.linked.reduce((s, a) => s + a.balance, 0))} on a linked account
              </span>
            )}
            {membership?.isMember && (
              <Pill variant="green">
                {membership.membershipName || "Member"} · {membershipPct}% off
              </Pill>
            )}
          </div>
        </div>
        <div className="grid-cols-1 sm:grid-cols-3" style={{ display: "grid", gap: 12 }}>
          <div>
            <label style={fieldLabel}>Name</label>
            <PatientSearchField
              value={customer.name}
              placeholder="Search or type name…"
              onChange={(v) => {
                setC("name", v);
                if (membership) setMembership(null);
              }}
              onPick={applyPatient}
              onAddNew={(text) => setAddPatient({ name: text, mobile: "" })}
            />
          </div>
          <div>
            <label style={fieldLabel}>Mobile</label>
            <PatientSearchField
              value={customer.mobile}
              mono
              placeholder="Search or type mobile…"
              onChange={(v) => {
                setC("mobile", v);
                /* A changed mobile invalidates a previously-looked-up member. */
                if (membership) setMembership(null);
              }}
              onPick={applyPatient}
              onAddNew={(text) => setAddPatient({ name: "", mobile: text })}
            />
          </div>
          <div>
            <label style={fieldLabel}>Doctor *</label>
            <select
              value={customer.doctorId || ""}
              onChange={(e) => {
                const v = e.target.value;
                /* "Add new doctor" routes to the Doctors master module. */
                if (v === "__add__") {
                  navigate(PHARMACY_ROUTES.DOCTORS);
                  return;
                }
                setC("doctorId", v);
              }}
              style={fieldInput}
            >
              <option value="">— Select doctor —</option>
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.speciality ? ` · ${d.speciality}` : ""}
                  {d.isDefault ? " (default)" : ""}
                </option>
              ))}
              <option value="__add__">＋ Add new doctor…</option>
            </select>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div style={{ background: "var(--bg-panel)", border: "1px solid var(--border-default)", borderRadius: 6, overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
            padding: "8px 12px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-subtle)",
          }}
        >
          <Eyebrow>Items · {lines.length}</Eyebrow>
          <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
            {pricing
              ? "Pricing…"
              : priceErr
                ? "Pricing unavailable"
                : `${cbFmtNum(bill.qty)} qty · GST ${cbFmtINR(bill.gst)} · Net ${cbFmtINR(
                  bill.payable
                )}`}
          </span>
        </div>
        {priceErr && (
          <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--red-700)", background: "var(--red-50)", borderBottom: "1px solid var(--border-default)" }}>
            {priceErr} — totals are provisional; billing is disabled until pricing succeeds.
          </div>
        )}
        {!priceErr && priced.warnings?.length > 0 && (
          <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--amber-700, var(--red-700))", background: "var(--amber-50, var(--bg-subtle))", borderBottom: "1px solid var(--border-default)" }}>
            {priced.warnings.map((w, i) => (
              <div key={i}>⚠ {w.message || w.code}</div>
            ))}
          </div>
        )}
        <div style={{ overflowX: "auto" }} className="cb-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 920 }}>
            <thead>
              <tr style={{ background: "var(--bg-subtle)" }}>
                {[
                  { l: "#" },
                  { l: "Product" },
                  { l: "Batch" },
                  { l: "Expiry" },
                  { l: "Qty", a: "right" },
                  { l: "UOM", a: "center" },
                  { l: "Disc %", a: "right" },
                  { l: "S.Price", a: "right" },
                  { l: "MRP", a: "right" },
                  { l: "Stock", a: "right" },
                  { l: "GST %", a: "right" },
                  { l: "Total", a: "right" },
                  { l: "" },
                ].map((h, i) => (
                  <th key={h.l || i} style={{ ...th, textAlign: h.a || "left" }}>
                    {h.l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={13} style={{ ...td, textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
                    No items yet — search a product below to start the bill.
                  </td>
                </tr>
              )}
              {bill.rows.map((r, i) => {
                const l = r.l;
                const c = r;
                const margin = billMarginPct(l);
                const isLoss = margin != null && margin.loss;
                const marginText =
                  margin == null || margin.pct == null
                    ? "—"
                    : `${margin.estimated ? "≈ " : ""}${margin.pct >= 0 ? "+" : ""}${trunc2(margin.pct)}%`;
                return (
                  <React.Fragment key={i}>
                    <tr style={{ background: editIdx === i ? "var(--ink-50)" : "transparent" }}>
                      <td style={{ ...td, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 600, color: "var(--fg-strong)" }}>
                        {l.medicineName}
                        {/* GX lines are the only ones CureCoin can be redeemed on. */}
                        {isGxLine(l) && (
                          <Pill variant="green" style={{ marginLeft: 6, height: 16, fontSize: 9 }}>
                            GX
                          </Pill>
                        )}
                        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)" }}>{l.medId}</div>
                      </td>
                      <td style={{ ...td, fontFamily: "var(--font-mono)" }}>
                        {l.batch}
                        {/* Where this batch sits — the same code that prints beside
                          the medicine name on the invoice. */}
                        {l.rack && (
                          <div style={{ fontSize: 10, color: "var(--fg-muted)" }}>Rack {l.rack}</div>
                        )}
                      </td>
                      <td style={{ ...td, fontFamily: "var(--font-mono)" }}>{l.exp || "—"}</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)" }}>{c.qty}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <Pill variant={l.uom === "unit" ? "amber" : "neutral"}>{uomLabel(l.uom)}</Pill>
                      </td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)" }}>{num(l.disc)}%</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                        {cbFmtINR(c.sale)}
                        {margin != null && (
                          <div
                            title={
                              (margin.estimated
                                ? `Estimated (reads low): no landed cost on record for this batch, so cost = PTR ${cbFmtINR(num(l.purchase))}`
                                : `Landed cost ${cbFmtINR(round2(num(l.landed)))}`) +
                              ` + ${num(l.gst)}% GST = ${cbFmtINR(round2(margin.cost))} per strip \u2192 S.Price ${cbFmtINR(num(l.salePrice))}` +
                              (num(l.disc) > 0 ? ` less ${num(l.disc)}% = ${cbFmtINR(round2(margin.realised))}` : "")
                            }
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: margin.loss ? "var(--red-700)" : margin.estimated ? "var(--amber-700)" : "var(--green-700)",
                            }}
                          >
                            {marginText}
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)" }}>{cbFmtINR(num(l.mrp))}</td>
                      {/* Stock as at the time the line was added — strips + loose, never a single figure. */}
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)", color: "var(--fg-muted)" }}>
                        {stockText(l)}
                      </td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)" }}>{num(l.gst)}%</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 600 }}>{cbFmtINR(c.net)}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <span style={{ display: "inline-flex", gap: 2 }}>
                          <button onClick={() => editLine(i)} title="Edit line" style={iconBtn("var(--ink-600)")}>
                            <IconReceipt size={13} />
                          </button>
                          <button onClick={() => delLine(i)} title="Remove line" style={iconBtn("var(--red-700)")}>
                            <IconX size={13} />
                          </button>
                        </span>
                      </td>
                    </tr>
                    {isLoss && (
                      <tr style={{ background: "var(--red-50)" }}>
                        <td />
                        <td colSpan={12} style={{ ...td, color: "var(--red-700)", fontSize: 11, fontWeight: 600 }}>
                          Selling below cost ({marginText}) — raise the S.Price or lower the Disc % to bill this item.
                        </td>
                      </tr>
                    )}
                    {!hasValidHsn(l) && (
                      <tr style={{ background: "var(--amber-50, var(--bg-subtle))" }}>
                        <td />
                        <td colSpan={12} style={{ ...td, color: "var(--amber-700)", fontSize: 11, fontWeight: 600 }}>
                          {String(l.hsn ?? "").trim()
                            ? `HSN "${String(l.hsn).trim()}" on this batch is not 6 or 8 digits`
                            : "No HSN on this batch"}
                          {" "}— the invoice will print it as is. You can still bill; report the batch to purchase for correction.
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add product */}
      <div style={{ background: "var(--bg-panel)", border: "1px solid var(--border-default)", borderRadius: 6, padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <Eyebrow>{editIdx !== null ? `Editing line ${editIdx + 1}` : "Add product"}</Eyebrow>
          {/* Batches are read per warehouse, so with none picked every product
              would come back "No stock" for no visible reason. */}
          {!warehouseId && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--amber-700)" }}>
              {isAll
                ? "“All stores” is selected — a sale comes out of one store's stock, so pick a single store in the topbar"
                : "Pick a store in the topbar to bill from its stock"}
            </span>
          )}
          {editIdx !== null && (
            <button
              onClick={() => {
                setEditIdx(null);
                setEntry(blankEntry());
              }}
              style={{ border: 0, background: "transparent", color: "var(--fg-link)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
            >
              Cancel edit
            </button>
          )}
        </div>

        <div
          className="grid-cols-2 sm:grid-cols-4 lg:grid-cols-[2fr_1.4fr_0.7fr_auto_0.6fr_0.9fr_0.6fr_auto]"
          style={{ display: "grid", gap: 10, alignItems: "end" }}
        >
          <div className="col-span-2 lg:col-span-1 min-w-0">
            <label style={fieldLabel}>Product</label>
            <MedicineSearchField
              value={entry.medicineName}
              warehouseId={warehouseId}
              onSelect={onSelectMedicine}
              onClear={() => onSelectMedicine(null)}
            />
          </div>
          <div className="col-span-2 lg:col-span-1 min-w-0">
            <label style={fieldLabel}>Batch (from stock)</label>
            <select value={entry.lotId} onChange={(e) => onPickBatch(e.target.value)} style={fieldInput} disabled={!entry.medId}>
              <option value="">
                {batchLoading
                  ? "Loading…"
                  : !warehouseId
                    ? "Pick a single store first"
                    : !entry.medId
                      ? "Pick a product"
                      : batches.length
                        ? "— Select batch —"
                        : "No stock"}
              </option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.batch} · Exp {b.exp} · ₹{b.sale || b.mrp} · {stockText(b)}
                  {b.rackCode ? ` · Rack ${b.rackCode}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={fieldLabel}>Qty</label>
            <input type="number" value={entry.qty} onChange={(e) => setE("qty", e.target.value)} style={fieldMono} />
          </div>
          <div>
            <label style={fieldLabel}>Sell by</label>
            {/* "Unit" is offered only when the picked batch supports loose sale
                (looseSaleSupported) — not inferred from loose stock or pack size. */}
            <Segmented
              options={UOM_OPTIONS.map((o) =>
                o.id === "unit" && !entry.looseSaleSupported
                  ? {
                    ...o,
                    disabled: true,
                    title: entry.lotId
                      ? "This batch cannot be sold by the unit"
                      : "Pick a batch to see if loose sale is allowed",
                  }
                  : o
              )}
              value={entry.uom}
              onChange={(v) => setE("uom", v)}
            />
          </div>
          <div>
            <label style={fieldLabel}>Disc %</label>
            <input type="number" value={entry.disc} onChange={(e) => setE("disc", e.target.value)} style={fieldMono} />
          </div>
          <div>
            <label style={fieldLabel}>{entryPerUnit > 1 ? "S.Price / unit" : "S.Price"}</label>
            <input
              type="number"
              value={entrySalePriceShown}
              onChange={(e) => setEntrySalePriceShown(e.target.value)}
              title={
                entryPerUnit > 1
                  ? `Price per loose unit. Strip price ${cbFmtINR(num(entry.salePrice))} ÷ ${entry.unitsPerStrip} units.`
                  : undefined
              }
              style={fieldMono}
            />
          </div>
          <div>
            <label style={fieldLabel}>GST %</label>
            <input type="number" value={entry.gst} onChange={(e) => setE("gst", e.target.value)} style={fieldMono} />
          </div>
          <Button variant="primary" icon={<IconPlus size={14} />} onClick={commitEntry} disabled={!entryValid}>
            {editIdx !== null ? "Update" : "Add"}
          </Button>
        </div>
        {entry.lotId && (
          <div style={{ marginTop: 8, fontSize: 11, color: "var(--fg-muted)" }}>
            Batch {entry.batch}: {stockText(entry)}
            {entry.unitsPerStrip ? ` · ${cbFmtNum(entry.unitsPerStrip)} units/strip` : ""} · max{" "}
            <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>{cbFmtNum(entryMaxQty)}</span>{" "}
            {entry.uom === "unit" ? "units" : "strips"}
          </div>
        )}
        {entry.medId && entry.lotId && num(entry.qty) > entryMaxQty && (
          <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: "var(--red-700)" }}>
            Qty exceeds available stock — {stockText(entry)} ={" "}
            {cbFmtNum(entryMaxQty)} {entry.uom === "unit" ? "units" : "strips"} sellable.
          </div>
        )}
      </div>

      {/* Totals + actions */}
      <div
        className="flex-wrap lg:flex-nowrap"
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          background: "var(--bg-panel)",
          border: "1px solid var(--border-default)",
          borderRadius: 6,
          padding: "12px 16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-end", gap: 22, fontSize: 12, flexWrap: "wrap" }}>
          <Total label="Gross" value={cbFmtINR(bill.gross)} />
          <Total label="Product disc" value={cbFmtINR(bill.prodDisc)} />
          <Total label="Membership" value={cbFmtINR(bill.member)} />
          <div>
            <label style={fieldLabel}>CureCoin ₹</label>
            <input
              type="number"
              value={cureCoin}
              onChange={(e) => setCureCoin(e.target.value)}
              placeholder="0"
              style={{ ...fieldMono, width: 90 }}
            />
            {/* CureCoin only redeems against GX lines — say so when the typed
                amount can't be applied (no GX item, or more than they owe). */}
            {num(cureCoin) > 0 && round2(num(cureCoin)) !== round2(bill.coin) && (
              <div style={{ fontSize: 10, color: "var(--amber-700)", marginTop: 2, maxWidth: 150 }}>
                {lines.some(isGxLine)
                  ? `Only ${cbFmtINR(bill.coin)} applied — GX lines' balance.`
                  : "No GX item on the bill — not applied."}
              </div>
            )}
          </div>
          <div>
            <label style={fieldLabel}>Payment mode</label>
            <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} style={{ ...fieldInput, width: 120 }}>
              {CB_PAYMENT_MODES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          {creditBalance > 0 && (
            <div>
              <label style={fieldLabel}>Store credit ₹</label>
              <div style={{ display: "flex", gap: 4 }}>
                <input
                  type="number"
                  min={0}
                  value={creditUse}
                  onChange={(e) => setCreditUse(e.target.value)}
                  placeholder="0"
                  style={{ ...fieldMono, width: 90 }}
                />
                <button
                  type="button"
                  onClick={() => setCreditUse(creditCap > 0 ? String(creditCap) : "")}
                  disabled={creditCap <= 0}
                  title={`Use up to ${cbFmtINR(creditCap)}`}
                  style={{
                    border: "1px solid var(--border-default)",
                    background: "var(--bg-panel)",
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "0 8px",
                    cursor: creditCap > 0 ? "pointer" : "not-allowed",
                    color: "var(--fg-link)",
                  }}
                >
                  Max
                </button>
              </div>
              <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 2 }}>
                Balance {cbFmtINR(creditBalance)}
              </div>
              {num(creditUse) > 0 && round2(num(creditUse)) !== creditApplied && (
                <div style={{ fontSize: 10, color: "var(--amber-700)", marginTop: 2, maxWidth: 150 }}>
                  Only {cbFmtINR(creditApplied)} applied — {creditCap < creditBalance ? "the bill's payable" : "the balance"}.
                </div>
              )}
            </div>
          )}
          <Total label="Taxable" value={cbFmtINR(bill.taxable)} />
          <Total label="GST" value={cbFmtINR(bill.gst)} />
          <Total label="Net payable" value={cbFmtINR(bill.payable)} strong />
          {creditApplied > 0 && (
            <Total
              label={balanceDue > 0 ? `Due by ${paymentMode}` : "Due"}
              value={cbFmtINR(balanceDue)}
              strong
            />
          )}
        </div>
        <div className="flex-wrap lg:flex-nowrap" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {hasLossLine && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--red-700)" }}>
              An item is priced below cost — fix it to create the bill
            </span>
          )}
          <Button onClick={reset} disabled={saving || (lines.length === 0 && !customer.name)}>
            Clear
          </Button>
          <Button
            onClick={handleComputeDiscount}
            disabled={memberLoading || !customer.mobile.trim()}
          >
            {memberLoading ? "Checking…" : "Compute discount"}
          </Button>
          <Button
            variant="primary"
            icon={<IconCheck size={14} />}
            onClick={handleCreate}
            disabled={!canCreate}
            style={{ height: "auto", minHeight: 30, maxWidth: "100%" }}
          >
            <span className="whitespace-normal sm:whitespace-nowrap py-1 sm:py-0">
              {saving ? "Billing…" : "Counter Sale · create bill"}
            </span>
          </Button>
        </div>
      </div>

      {invoicePreview && (
        <InvoicePreviewModal
          invoice={invoicePreview}
          printing={printingInvoice}
          onPrint={handlePreviewPrint}
          onClose={handlePreviewClose}
        />
      )}

      {addPatient && (
        <AddPatientModal
          initial={addPatient}
          onClose={() => setAddPatient(null)}
          onCreated={(p) => {
            applyPatient(p);
            setAddPatient(null);
          }}
        />
      )}

    </div>
  );
};

/* In-app preview of the just-posted counter-sale bill. The HTML body is the
   same one openPrintWindow prints and invoiceToPdfBlob renders to a PDF —
   shown inside an iframe so its <style> block can't bleed into the app.
   Mirrors AllocationBilling's InvoicePreviewModal. */
const InvoicePreviewModal = ({ invoice, printing, onPrint, onClose }) => {
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${invoice.styles}</style></head><body>${invoice.html}</body></html>`;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,18,22,0.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      className="p-2 sm:p-6"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-panel)",
          borderRadius: 8,
          maxWidth: 980,
          width: "100%",
          height: "92vh",
          maxHeight: "92dvh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--border-default)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--fg-strong)" }}>
              Bill preview · {invoice.billNo}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
              Counter sale posted. Click Print to save the PDF and open the print dialog.
            </div>
          </div>
          <Button onClick={onClose} disabled={printing}>
            Close
          </Button>
          <Button variant="primary" icon={<IconReceipt size={14} />} onClick={onPrint} disabled={printing}>
            {printing ? "Preparing…" : "Print"}
          </Button>
        </div>
        <iframe
          title={`Bill ${invoice.billNo}`}
          srcDoc={srcDoc}
          style={{ border: 0, width: "100%", flex: 1, background: "#fff" }}
        />
      </div>
    </div>
  );
};

const Total = ({ label, value, strong }) => (
  <div>
    <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--fg-muted)" }}>{label}</div>
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: strong ? 16 : 13,
        fontWeight: 700,
        marginTop: 2,
        color: strong ? "var(--ink-700)" : "var(--fg-strong)",
      }}
    >
      {value}
    </div>
  </div>
);

/* Typeahead for the customer Name / Mobile fields — searches the patient
   service (che-services patient/search) after 3+ chars (debounced) and, on
   pick, auto-fills the customer's name + mobile. Both fields share this so a
   match can be found by either. When nothing matches, offers "Add new patient"
   which opens the AddPatientModal prefilled with what was typed. `onChange`
   keeps the free-typed text in the parent (name/mobile is used as-is when the
   cashier doesn't pick a suggestion, e.g. a plain walk-in). */
const PatientSearchField = ({ value, onChange, onPick, onAddNew, mono, placeholder }) => {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = React.useRef(null);

  useEffect(() => {
    setQ(value || "");
  }, [value]);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      supplyService
        .searchPatients(trimmed)
        .then((items) => {
          setResults(items || []);
          setLoading(false);
        })
        .catch(() => {
          setResults([]);
          setLoading(false);
        });
    }, 300);
    return () => clearTimeout(handle);
  }, [q]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (p) => {
    onPick(p);
    setOpen(false);
  };

  const showDropdown = open && q.trim().length >= 3;
  const inputStyle = mono ? { ...fieldInput, fontFamily: "var(--font-mono)" } : fieldInput;

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          onChange(e.target.value);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || "Type 3+ chars to search…"}
        style={inputStyle}
      />
      {showDropdown && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 2,
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            boxShadow: "0 8px 20px rgba(13,17,27,0.12)",
            maxHeight: 260,
            overflowY: "auto",
            zIndex: 30,
          }}
        >
          {loading && <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--fg-muted)" }}>Searching…</div>}
          {!loading &&
            results.map((p) => (
              <div
                key={p.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(p)}
                style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-subtle)", fontSize: 12 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-subtle)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ fontWeight: 600, color: "var(--fg-strong)" }}>
                  {[p.salutation, patientFullName(p)].filter(Boolean).join(" ")}
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)", marginTop: 2 }}>
                  {p.mobile || "—"}
                  {p.code ? ` · ${p.code}` : ""}
                </div>
              </div>
            ))}
          {!loading && (
            <div
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onAddNew(q.trim());
                setOpen(false);
              }}
              style={{
                padding: "8px 10px",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--fg-link)",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-subtle)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <IconPlus size={13} />
              {results.length ? "Add new patient" : `No match — add new patient`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* Age from a yyyy-MM-dd date of birth. */
const ageFromDob = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  if (!y || !m || !d) return "";
  const now = new Date();
  let age = now.getFullYear() - y;
  const beforeBirthday =
    now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d);
  if (beforeBirthday) age -= 1;
  return age >= 0 && age < 150 ? String(age) : "";
};
/* Approximate yyyy-MM-dd from an age (today minus N years) — used when the
   operator knows the age but not the exact DOB. */
const dobFromAge = (age) => {
  const a = Number(age);
  if (!Number.isFinite(a) || a < 0 || a > 150) return "";
  const now = new Date();
  const y = now.getFullYear() - a;
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
};
/* yyyy-MM-dd (date input) -> dd-MM-yyyy (register API format). */
const toDdMmYyyy = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  const [y, m, d] = yyyyMmDd.split("-");
  return y && m && d ? `${d}-${m}-${y}` : "";
};
const SALUTATIONS = ["Mr.", "Mrs.", "Ms.", "Dr.", "Master", "Baby", "Mx."];
const todayISO = () => new Date().toISOString().slice(0, 10);

/* Popup to register a walk-in customer the search didn't find. Full patient form
   with mobile-OTP verification (D2C/DHP): the operator sends an OTP, verifies it,
   and only then can register. On success the created record is handed back so
   Bill Entry can fill the customer strip. */
const AddPatientModal = ({ initial, onClose, onCreated }) => {
  const initialMobile = /^\d{10}$/.test(String(initial?.name || "")) ? initial.name : initial?.mobile || "";
  const [salutation, setSalutation] = useState("Mr.");
  const [firstName, setFirstName] = useState(
    /^\d{10}$/.test(String(initial?.name || "")) ? "" : initial?.name || ""
  );
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("M");
  const [dob, setDob] = useState("");
  const [age, setAge] = useState("");

  const [mobile, setMobile] = useState(initialMobile);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [saving, setSaving] = useState(false);

  const mobileValid = /^\d{10}$/.test(mobile.trim());

  const onMobileChange = (v) => {
    setMobile(v.replace(/\D/g, "").slice(0, 10));
    /* Any change to the mobile invalidates a prior OTP send/verification. */
    setOtpSent(false);
    setOtpVerified(false);
    setOtp("");
  };
  const onDobChange = (v) => {
    setDob(v);
    setAge(ageFromDob(v));
  };
  const onAgeChange = (v) => {
    const a = v.replace(/\D/g, "").slice(0, 3);
    setAge(a);
    setDob(a ? dobFromAge(a) : "");
  };

  const sendOtp = async () => {
    if (!mobileValid || sendingOtp) return;
    setSendingOtp(true);
    try {
      await supplyService.sendPatientOtp(mobile.trim());
      setOtpSent(true);
      toast.success(`OTP sent to ${mobile.trim()}.`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not send OTP.");
    } finally {
      setSendingOtp(false);
    }
  };
  const verifyOtp = async () => {
    if (!otp.trim() || verifyingOtp) return;
    setVerifyingOtp(true);
    try {
      await supplyService.validatePatientOtp(mobile.trim(), otp.trim());
      setOtpVerified(true);
      toast.success("Mobile verified.");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "OTP verification failed.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const detailsValid =
    !!salutation && firstName.trim().length > 0 && !!age && !!dob && (gender === "M" || gender === "F");
  /* OTP verification is optional — it confirms the number reaches the patient,
     but the counter often registers someone standing in front of them and must
     not be blocked. A valid 10-digit mobile is still required, since otpVerified
     used to be what implicitly guaranteed one. */
  const canRegister = mobileValid && detailsValid && !saving;

  const submit = async () => {
    if (!canRegister) return;
    setSaving(true);
    try {
      const created = await supplyService.registerPatientDhp({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        mobileNumber: mobile.trim(),
        dob: toDdMmYyyy(dob),
        salutation,
        gender,
      });
      toast.success("Patient registered.");
      onCreated({
        ...(created || {}),
        salutation: created?.salutation || salutation,
        firstName: created?.firstName || firstName.trim(),
        lastName: created?.lastName || lastName.trim(),
        mobile: created?.mobile || created?.mobileNumber || mobile.trim(),
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not register the patient.");
    } finally {
      setSaving(false);
    }
  };

  const smallBtn = {
    height: 34,
    padding: "0 12px",
    borderRadius: 4,
    border: "1px solid var(--border-default)",
    background: "var(--bg-panel)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    color: "var(--fg-default)",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,18,22,0.55)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      className="p-2 sm:p-6"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-panel)",
          borderRadius: 8,
          width: 520,
          maxWidth: "100%",
          maxHeight: "min(92vh, 92dvh)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-default)" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--fg-strong)" }}>Add new patient</div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
            Verifying the mobile with an OTP is optional.
          </div>
        </div>

        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, overflow: "auto" }} className="cb-scroll">
          {/* Mobile + OTP verification */}
          <div>
            <label style={fieldLabel}>Mobile *</label>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                autoFocus
                value={mobile}
                onChange={(e) => onMobileChange(e.target.value)}
                placeholder="10-digit mobile"
                disabled={otpVerified}
                style={{ ...fieldInput, fontFamily: "var(--font-mono)", flex: 1, minWidth: 0, opacity: otpVerified ? 0.7 : 1 }}
              />
              {otpVerified ? (
                <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: "var(--green-700)", padding: "0 8px" }}>
                  <IconCheck size={14} /> Verified
                </span>
              ) : (
                <button type="button" onClick={sendOtp} disabled={!mobileValid || sendingOtp} style={{ ...smallBtn, opacity: !mobileValid || sendingOtp ? 0.5 : 1 }}>
                  {sendingOtp ? "Sending…" : otpSent ? "Resend OTP" : "Send OTP"}
                </button>
              )}
            </div>
            {mobile.trim() && !mobileValid && (
              <div style={{ marginTop: 4, fontSize: 10, fontWeight: 600, color: "var(--red-700)" }}>
                Enter a valid 10-digit mobile.
              </div>
            )}
          </div>

          {otpSent && !otpVerified && (
            <div>
              <label style={fieldLabel}>Enter OTP *</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="OTP"
                  inputMode="numeric"
                  style={{ ...fieldInput, fontFamily: "var(--font-mono)", flex: 1, minWidth: 0 }}
                />
                <button type="button" onClick={verifyOtp} disabled={!otp.trim() || verifyingOtp} style={{ ...smallBtn, opacity: !otp.trim() || verifyingOtp ? 0.5 : 1 }}>
                  {verifyingOtp ? "Verifying…" : "Verify OTP"}
                </button>
              </div>
            </div>
          )}

          {/* Identity */}
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 10 }}>
            <div>
              <label style={fieldLabel}>Salutation *</label>
              <select value={salutation} onChange={(e) => setSalutation(e.target.value)} style={fieldInput}>
                {SALUTATIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={fieldLabel}>First name *</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" style={fieldInput} />
            </div>
          </div>
          <div>
            <label style={fieldLabel}>Last name</label>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" style={fieldInput} />
          </div>

          {/* DOB / Age / Gender */}
          <div className="grid-cols-2 sm:grid-cols-[1fr_90px_110px]" style={{ display: "grid", gap: 10 }}>
            <div className="col-span-2 sm:col-span-1">
              <label style={fieldLabel}>Date of birth</label>
              <input type="date" max={todayISO()} value={dob} onChange={(e) => onDobChange(e.target.value)} style={{ ...fieldInput, fontFamily: "var(--font-mono)" }} />
            </div>
            <div>
              <label style={fieldLabel}>Age *</label>
              <input value={age} onChange={(e) => onAgeChange(e.target.value)} placeholder="Age" inputMode="numeric" style={{ ...fieldInput, fontFamily: "var(--font-mono)" }} />
            </div>
            <div>
              <label style={fieldLabel}>Gender *</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)} style={fieldInput}>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
          </div>

          <div>
            <label style={fieldLabel}>Email (optional)</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="name@example.com" style={fieldInput} />
          </div>

          {!otpVerified && (
            <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
              {mobileValid
                ? "Mobile not verified — you can still register. Send an OTP to confirm the number reaches the patient."
                : "Enter a 10-digit mobile to enable registration. OTP verification is optional."}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border-default)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" icon={<IconCheck size={14} />} onClick={submit} disabled={!canRegister}>
            {saving ? "Registering…" : "Register patient"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const dropdownBox = {
  position: "absolute",
  top: "100%",
  left: 0,
  marginTop: 2,
  minWidth: "100%",
  background: "var(--bg-panel)",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  boxShadow: "0 8px 20px rgba(13,17,27,0.12)",
  zIndex: 30,
};

/* Product search for the counter — reads live stock, not the catalogue.
 *
 *   GET v1/inventory/warehouse/{wh}?search=…
 *
 * Deliberately the stock table and not the ES catalogue: at the till the only
 * products worth offering are the ones on the shelf, and the same rows carry the
 * batches, so picking a product fills the batch list without a second call.
 * The catalogue is still consulted once, after the pick, for the GX flag.
 *
 * Rows are one per product; the stock column is "strips:loose (total units)". */
const SEARCH_COLS = [
  { key: "name", label: "Product", width: "minmax(220px, 1.6fr)" },
  { key: "mrp", label: "MRP", width: "88px", right: true },
  { key: "stock", label: "Stock", width: "120px", right: true },
  { key: "ratio", label: "U.Ratio", width: "68px", right: true },
  /* Rack codes run longer than they look — "RACKPICKUP" truncates at 78px. */
  { key: "rack", label: "Rack", width: "104px" },
  { key: "pack", label: "Pack", width: "84px" },
];
const SEARCH_GRID = SEARCH_COLS.map((c) => c.width).join(" ");

const cellBase = {
  padding: "7px 10px",
  fontSize: 12,
  color: "var(--fg-default)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const MedicineSearchField = ({ value, warehouseId, onSelect, onClear }) => {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  /* GX flag per medicine code, for the badge on each row. Kept beside the
     results rather than merged into them so a re-search doesn't drop the flags
     already resolved — they're cached in the service and keyed by code. */
  const [gxByCode, setGxByCode] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  /* Keyboard-first: the counter types, arrows, Enter. The pointer is the slow
     path here, so the active row is tracked explicitly rather than left to hover. */
  const [active, setActive] = useState(0);
  const boxRef = React.useRef(null);
  const listRef = React.useRef(null);

  useEffect(() => {
    setQ(value || "");
  }, [value]);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      supplyService
        .searchInventoryProducts(trimmed, { warehouseId })
        .then((items) => {
          setResults(items || []);
          setActive(0);
          setLoading(false);
        })
        .catch(() => {
          setResults([]);
          setLoading(false);
        });
    }, 250);
    return () => clearTimeout(handle);
  }, [q, warehouseId]);

  /* GX is a catalogue attribute and the stock rows don't carry it, so the flags
     for the current results are fetched alongside and merged in as they land.
     Badge only — the flag the billed line keeps is fetched again on pick. */
  useEffect(() => {
    if (!results.length) return undefined;
    let alive = true;
    supplyService
      .getPreferredMedicineFlags(results.map((p) => p.medId))
      .then((map) => {
        if (alive) setGxByCode((prev) => ({ ...prev, ...map }));
      })
      .catch(() => { });
    return () => {
      alive = false;
    };
  }, [results]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  /* Keep the active row inside the scroll box when arrowing past its edge. */
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-row="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, results]);

  const pick = (p) => {
    onSelect(p);
    setQ(p.medicineName);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && q.trim().length >= 3;
  const showHint = open && q.trim().length > 0 && q.trim().length < 3;

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          if (!e.target.value.trim() && onClear) onClear();
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Type 3+ chars to search stock…"
        style={fieldInput}
      />
      {showHint && (
        <div style={{ ...dropdownBox, padding: "6px 10px", fontSize: 11, color: "var(--fg-muted)" }}>
          Type at least 3 characters to search.
        </div>
      )}
      {showDropdown && (
        <div ref={listRef} style={{ ...dropdownBox, width: "max(100%, 720px)", maxWidth: "min(92vw, calc(100vw - 32px))", maxHeight: 300, overflowY: "auto" }}>
          {/* Column heads stay put while the list scrolls — six columns of
              numbers are unreadable once their labels have scrolled away. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: SEARCH_GRID,
              position: "sticky",
              top: 0,
              zIndex: 1,
              background: "var(--bg-subtle)",
              borderBottom: "1px solid var(--border-default)",
            }}
          >
            {SEARCH_COLS.map((c) => (
              <div
                key={c.key}
                style={{
                  ...cellBase,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "var(--fg-muted)",
                  textAlign: c.right ? "right" : "left",
                }}
              >
                {c.label}
              </div>
            ))}
          </div>

          {loading && <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--fg-muted)" }}>Searching stock…</div>}
          {!loading && !warehouseId && (
            <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--fg-muted)" }}>
              Pick a store in the top bar to search its stock.
            </div>
          )}
          {!loading && warehouseId && results.length === 0 && (
            <div style={{ padding: "8px 10px", fontSize: 11, color: "var(--fg-muted)" }}>
              Nothing in stock matches “{q.trim()}”.
            </div>
          )}

          {!loading &&
            results.map((p, i) => (
              <div
                key={p.medId}
                data-row={i}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(p)}
                onMouseEnter={() => setActive(i)}
                style={{
                  display: "grid",
                  gridTemplateColumns: SEARCH_GRID,
                  alignItems: "center",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--border-subtle)",
                  background: i === active ? "var(--bg-selected)" : "transparent",
                }}
              >
                <div style={cellBase}>
                  <div style={{ fontWeight: 600, color: "var(--fg-strong)", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {p.medicineName}
                    {/* One badge, GX first. GX is what the counter is looking
                        for — it's the only thing CureCoin redeems against — so on
                        a product that is both it wins the slot and RX is dropped.
                        The flag lands a moment after the rows, so an RX+GX row
                        shows RX briefly and then flips. */}
                    {isGxCode(gxByCode[p.medId]) ? (
                      <Pill variant="green" style={{ marginLeft: 6, height: 15, fontSize: 9 }}>
                        GX
                      </Pill>
                    ) : (
                      String(p.drugCategory).toUpperCase() === "RX" && (
                        <Pill variant="amber" style={{ marginLeft: 6, height: 15, fontSize: 9 }}>
                          RX
                        </Pill>
                      )
                    )}
                  </div>
                  {/* The FEFO batch — the one auto-picked on select, and the one
                      this row's MRP describes. Named here so the counter reads
                      what they are about to bill before picking it, not after.
                      Truncates rather than wrapping: a second line would push the
                      rows apart and cost more than the tail of a batch code. */}
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      color: "var(--fg-muted)",
                      marginTop: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.medId}
                    {p.batches[0]?.batch ? ` · ${p.batches[0].batch}` : ""}
                    {p.batches[0]?.exp ? ` · Exp ${p.batches[0].exp}` : ""}
                    {/* The others are one click away in the batch picker, so say
                        how many rather than implying this is all there is. */}
                    {p.batches.length > 1 ? ` · +${p.batches.length - 1} more` : ""}
                  </div>
                </div>
                <div style={{ ...cellBase, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                  {cbFmtINR(p.mrp)}
                  {/* One number cannot speak for batches priced differently. */}
                  {p.mrpVaries && <span style={{ color: "var(--fg-muted)" }}>+</span>}
                </div>
                <div style={{ ...cellBase, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                  {p.strips}
                  {p.loose ? `:${p.loose}` : ""}
                  <span style={{ color: "var(--fg-muted)" }}> ({cbFmtNum(p.units)})</span>
                </div>
                <div style={{ ...cellBase, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                  {p.unitsPerStrip || "—"}
                </div>
                {/* A HOME rack is where the medicine belongs, not where this batch
                    was seen — shown muted so a guess never reads as a fact. */}
                <div
                  style={{
                    ...cellBase,
                    fontFamily: "var(--font-mono)",
                    color: p.rackSource === "HOME" ? "var(--fg-muted)" : "var(--fg-default)",
                  }}
                >
                  {p.rackCode || "—"}
                </div>
                <div style={{ ...cellBase, color: "var(--fg-muted)" }}>{p.unitPack || "—"}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default SalesBillEntry;
